import React, { useState, useRef } from 'react';
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogContent,
  Divider,
} from '@mui/material';
import {
  EditBase,
  SaveButton,
  SimpleForm,
  Toolbar,
  useNotify,
  useRecordContext,
  useRedirect,
  useUpdate,
  useGetOne,
} from 'react-admin';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useDebounceEffect } from '../components/useDebounceEffect';
import { canvasPreview } from '../components/CanvasPreview';
import { useFormContext } from 'react-hook-form';
import { Funeral, Client } from '../types';
import { Identifier } from 'react-admin';

// Output size for the final image
const OUTPUT_SIZE = 320;

// Always use 1:1 aspect ratio for circular crop
const ASPECT_RATIO = 1;

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 80,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

// Inner form content component that has access to form context
const ImageUploadFormContent = ({
  previewCanvasRef,
  imgRef,
  imgSrc,
  setImgSrc,
  crop,
  setCrop,
  completedCrop,
  setCompletedCrop,
  scale,
  rotate,
  onCloseClick,
  clientId,
  funeralId,
}: {
  previewCanvasRef: React.RefObject<HTMLCanvasElement>;
  imgRef: React.RefObject<HTMLImageElement>;
  imgSrc: string;
  setImgSrc: (src: string) => void;
  crop: Crop | undefined;
  setCrop: (crop: Crop | undefined) => void;
  completedCrop: PixelCrop | undefined;
  setCompletedCrop: (crop: PixelCrop) => void;
  scale: number;
  rotate: number;
  onCloseClick: () => void;
  clientId: Identifier;
  funeralId: Identifier;
}) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [update] = useUpdate();
  const { getValues } = useFormContext();

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () =>
        setImgSrc(reader.result?.toString() || '')
      );
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    // Auto-center the crop when image loads
    setCrop(centerAspectCrop(width, height, ASPECT_RATIO));
  }

  const onSuccess = () => {
    notify(`Client image saved`);
    onCloseClick();
    redirect(false);
  };

  const transformData = (data: any) => {
    const srcCanvas = previewCanvasRef.current;
    if (!srcCanvas) {
      throw new Error('Crop canvas does not exist');
    }

    // canvasPreview() renders the crop at its FULL source resolution ×
    // devicePixelRatio, so a phone photo can produce a 6000×6000 canvas. As a
    // lossless PNG that's tens of MB and exceeds Supabase's upload size limit
    // (Chrome uploads the giant PNG and gets rejected; WebKit clamps canvas
    // area so it happened to squeak through). Downscale to a bounded square and
    // encode JPEG — plenty sharp for a 320px circular avatar, ~tens of KB.
    const MAX_OUTPUT = 640;
    const outSize = Math.min(MAX_OUTPUT, srcCanvas.width, srcCanvas.height) || MAX_OUTPUT;
    const outCanvas = document.createElement('canvas');
    outCanvas.width = outSize;
    outCanvas.height = outSize;
    const octx = outCanvas.getContext('2d');
    if (!octx) {
      throw new Error('Could not get output canvas context');
    }
    octx.imageSmoothingQuality = 'high';
    octx.drawImage(srcCanvas, 0, 0, srcCanvas.width, srcCanvas.height, 0, 0, outSize, outSize);

    const base64String = outCanvas.toDataURL('image/jpeg', 0.85);

    return {
      ...data,
      client_image: base64String,
    };
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    
    if (!previewCanvasRef.current || !completedCrop) {
      notify('Please select and crop an image first', { type: 'warning' });
      return;
    }
    
    const formValues = getValues();
    const transformedData = transformData(formValues);
    // Include IDs in the data - the data provider expects these for the file path
    transformedData.id = clientId;
    transformedData.funeral_id = funeralId;
    // Update the client record with the image
    await update('clients', { id: clientId, data: transformedData }, { onSuccess: () => onSuccess() });
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // Set canvas to output size (320x320)
        previewCanvasRef.current.width = OUTPUT_SIZE;
        previewCanvasRef.current.height = OUTPUT_SIZE;
        
        await canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" component="label">
          Choose Image
          <input type="file" hidden accept="image/*" onChange={onSelectFile} />
        </Button>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          Select an image, then drag the circular crop to center the face
        </Typography>
      </Box>
      
      {!!imgSrc && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Drag the circle to position the crop:
          </Typography>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={ASPECT_RATIO}
            circularCrop
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imgSrc}
              crossOrigin="anonymous"
              style={{
                transform: `scale(${scale}) rotate(${rotate}deg)`,
                maxWidth: '100%',
                maxHeight: '400px',
              }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        </Box>
      )}
      
      {!!completedCrop && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Preview (as it will appear on the website):
          </Typography>
          <Box
            sx={{
              display: 'inline-block',
              borderRadius: '50%',
              border: '4px solid white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              width: OUTPUT_SIZE,
              height: OUTPUT_SIZE,
              backgroundColor: '#f5f5f5',
            }}
          >
            <canvas
              ref={previewCanvasRef}
              style={{
                display: 'block',
                width: OUTPUT_SIZE,
                height: OUTPUT_SIZE,
                borderRadius: '50%',
              }}
            />
          </Box>
        </Box>
      )}
      
      <Toolbar>
        <SaveButton
          label="Save"
          type="button"
          alwaysEnable={!!completedCrop}
          disabled={!completedCrop}
          onClick={handleSubmit}
        />
      </Toolbar>
    </>
  );
};

export const DeceasedClientImageUpload = () => {
  const funeralRecord = useRecordContext<Funeral>();
  const [showPanel, setShowPanel] = useState(false);

  const [imgSrc, setImgSrc] = useState('');
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale] = useState(1);
  const [rotate] = useState(0);

  // Get the deceased client data
  const { data: client, isLoading } = useGetOne<Client>(
    'clients',
    { id: funeralRecord?.deceased_client_id },
    { enabled: !!funeralRecord?.deceased_client_id }
  );

  const handleEditClick = async () => {
    if (client?.image_path) {
      const existingUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/memorial-images/${client.image_path}`;
      try {
        const resp = await fetch(existingUrl);
        const blob = await resp.blob();
        const reader = new FileReader();
        reader.onload = () => setImgSrc(reader.result?.toString() || '');
        reader.readAsDataURL(blob);
      } catch {
        setImgSrc(existingUrl);
      }
    }
    setShowPanel(true);
  };

  const handleAddClick = () => {
    setShowPanel(true);
  };

  const handleCloseClick = () => {
    setShowPanel(false);
    // Reset state when closing
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  if (!funeralRecord?.deceased_client_id) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Client Image</Typography>
        <Divider sx={{ mb: 1 }} />
        <Typography color="textSecondary">
          No deceased client selected
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Client Image</Typography>
        <Divider sx={{ mb: 1 }} />
        <Typography color="textSecondary">Loading...</Typography>
      </Box>
    );
  }

  const imageSrc = client?.image_path
    ? `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/memorial-images/${client.image_path}`
    : null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6">Client Image</Typography>
      <Divider sx={{ mb: 1 }} />
      
      {imageSrc && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'inline-block',
              borderRadius: '50%',
              border: '4px solid white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              width: 200,
              height: 200,
            }}
          >
            <img 
              src={imageSrc} 
              alt="Client" 
              style={{ 
                width: 200, 
                height: 200, 
                objectFit: 'cover',
                display: 'block',
              }} 
            />
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        {imageSrc && (
          <Button color="primary" variant="contained" onClick={handleEditClick}>
            Edit Image
          </Button>
        )}
        <Button color="primary" variant="outlined" onClick={handleAddClick}>
          {imageSrc ? 'Replace Image' : 'Add Client Image'}
        </Button>
      </Box>

      <Dialog open={showPanel} onClose={handleCloseClick} disableEnforceFocus maxWidth="sm" fullWidth>
        <DialogContent>
          {client && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Upload Image for {client.name}
              </Typography>
              <EditBase resource="clients" id={client.id}>
                <SimpleForm toolbar={false}>
                  <ImageUploadFormContent
                    previewCanvasRef={previewCanvasRef}
                    imgRef={imgRef}
                    imgSrc={imgSrc}
                    setImgSrc={setImgSrc}
                    crop={crop}
                    setCrop={setCrop}
                    completedCrop={completedCrop}
                    setCompletedCrop={setCompletedCrop}
                    scale={scale}
                    rotate={rotate}
                    onCloseClick={handleCloseClick}
                    clientId={client.id}
                    funeralId={funeralRecord.id}
                  />
                </SimpleForm>
              </EditBase>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DeceasedClientImageUpload;
